import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  CheckCircle2Icon,
  FileIcon,
  InboxIcon,
  InfoIcon,
  PencilLineIcon,
  RotateCwIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import EditFileDetailsView from "@/components/EditFileDetailsView";
import type { EditFileDetailsViewHandle } from "@/components/EditFileDetailsView";
import { cn } from "@/utils/cn";
import { documentsApi } from "@/services/rag-service";
import { getIntlLocale } from "@/utils/language";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  SUPPORTED_DOCUMENT_EXTENSIONS,
} from "@/utils/upload-file-types";
import {
  MAX_UPLOAD_FILES,
  MAX_UPLOAD_FILE_SIZE_MB,
  createDocumentDetails,
  formatFileSize,
  getFileIdentity,
  hasExtraDetails,
  runWithConcurrency,
  titleFromFileName,
  toUploadMetadata,
  validateUploadFile,
} from "@/utils/document-upload-queue";
import type {
  DocumentDetails,
  UploadFileProblem,
} from "@/utils/document-upload-queue";

// Each file is its own POST; a few at a time keeps the queue moving without
// saturating the connection or the ingestion service.
const UPLOAD_CONCURRENCY = 3;

type UploadStatus = "pending" | "uploading" | "done" | "error" | "invalid";

type UploadItem = {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  problem?: UploadFileProblem;
  errorMessage?: string;
  details: DocumentDetails;
};

// Files whose details can still change: not uploaded, not uploading, not rejected.
const isEditable = (item: UploadItem) =>
  item.status === "pending" || item.status === "error";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  /** Called once per batch when at least one document was uploaded. */
  onUploaded: () => void;
};

const getUploadErrorMessage = (err: unknown) => {
  const error = err as {
    message?: string;
    response?: { data?: { error?: { message?: unknown }; detail?: unknown } };
  };
  const apiMessage =
    error.response?.data?.error?.message ?? error.response?.data?.detail;
  if (typeof apiMessage === "string" && apiMessage) return apiMessage;
  return error.message || "";
};

const UploadDocumentsDialog = ({
  open,
  onOpenChange,
  collectionId,
  onUploaded,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const editViewRef = useRef<EditFileDetailsViewHandle>(null);
  const controllersRef = useRef(new Map<string, AbortController>());
  // Read after a batch settles, when the closure's `items` is already stale.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Start every open with an empty queue.
  useEffect(() => {
    if (open) {
      setItems([]);
      setIsDragging(false);
      setIsEditingDetails(false);
    }
  }, [open]);

  const updateItem = useCallback(
    (id: string, patch: Partial<UploadItem>) =>
      setItems((current) =>
        current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      ),
    [],
  );

  const addFiles = useCallback(
    (fileList: FileList | File[]) => {
      const incoming = Array.from(fileList);
      if (incoming.length === 0) return;

      const known = new Set(items.map((item) => item.id));
      const fresh = incoming.filter((file) => {
        const id = getFileIdentity(file);
        if (known.has(id)) return false;
        known.add(id);
        return true;
      });

      const room = MAX_UPLOAD_FILES - items.length;
      if (fresh.length > room) {
        toast.warning(
          t("collections.multiUpload.tooManyFiles", {
            max: MAX_UPLOAD_FILES,
          }),
        );
      }

      const added: UploadItem[] = fresh
        .slice(0, Math.max(room, 0))
        .map((file) => {
          const problem = validateUploadFile(file);
          return {
            id: getFileIdentity(file),
            file,
            status: problem ? "invalid" : "pending",
            progress: 0,
            problem: problem ?? undefined,
            details: createDocumentDetails(file.name),
          };
        });

      if (added.length > 0) setItems([...items, ...added]);
    },
    [items, t],
  );

  const removeItem = (id: string) =>
    setItems((current) => current.filter((item) => item.id !== id));

  const cancelItem = (id: string) => controllersRef.current.get(id)?.abort();

  const uploadOne = useCallback(
    async (item: UploadItem) => {
      const controller = new AbortController();
      controllersRef.current.set(item.id, controller);
      updateItem(item.id, {
        status: "uploading",
        progress: 0,
        errorMessage: undefined,
      });

      try {
        await documentsApi.uploadDocument(
          item.file,
          item.details.title.trim() || titleFromFileName(item.file.name),
          collectionId,
          toUploadMetadata(item.details),
          {
            signal: controller.signal,
            onUploadProgress: (event: { loaded: number; total?: number }) => {
              const total = event.total || item.file.size;
              if (!total) return;
              updateItem(item.id, {
                progress: Math.min(
                  100,
                  Math.round((event.loaded / total) * 100),
                ),
              });
            },
          },
        );
        updateItem(item.id, { status: "done", progress: 100 });
        return true;
      } catch (err) {
        updateItem(item.id, {
          status: "error",
          progress: 0,
          errorMessage: controller.signal.aborted
            ? t("collections.multiUpload.cancelled")
            : getUploadErrorMessage(err) ||
              t("collections.failedToUploadDocument"),
        });
        return false;
      } finally {
        controllersRef.current.delete(item.id);
      }
    },
    [collectionId, t, updateItem],
  );

  const uploadBatch = useCallback(
    async (batch: UploadItem[]) => {
      if (batch.length === 0) return;
      setIsUploading(true);

      let succeeded = 0;
      await runWithConcurrency(batch, UPLOAD_CONCURRENCY, async (item) => {
        if (await uploadOne(item)) succeeded += 1;
      });

      setIsUploading(false);
      if (succeeded > 0) onUploaded();

      const failed = batch.length - succeeded;
      if (failed === 0) {
        toast.success(
          t("collections.multiUpload.uploadedCount", { count: succeeded }),
        );
        // Close once every file in the list is uploaded; rejected or failed
        // files keep the dialog open so they can be retried or removed.
        const batchIds = new Set(batch.map((item) => item.id));
        if (
          itemsRef.current.every(
            (item) => batchIds.has(item.id) || item.status === "done",
          )
        ) {
          onOpenChange(false);
        }
      } else {
        toast.error(
          t("collections.multiUpload.partialFailure", {
            failed,
            total: batch.length,
          }),
        );
      }
    },
    [onOpenChange, onUploaded, t, uploadOne],
  );

  const handleUploadAll = () => uploadBatch(items.filter(isEditable));

  const handleSaveDetails = (details: Record<string, DocumentDetails>) => {
    setItems((current) =>
      current.map((item) =>
        details[item.id] ? { ...item, details: details[item.id] } : item,
      ),
    );
    setIsEditingDetails(false);
  };

  const handleRetry = (item: UploadItem) => uploadBatch([item]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      controllersRef.current.forEach((controller) => controller.abort());
    }
    onOpenChange(nextOpen);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) addFiles(event.target.files);
    // Reset so picking the same file again after removing it still fires.
    event.target.value = "";
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  const dateFormatter = new Intl.DateTimeFormat(getIntlLocale(i18n.language), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const supportedTypes = SUPPORTED_DOCUMENT_EXTENSIONS.map((ext) =>
    ext.slice(1).toUpperCase(),
  ).join(", ");
  const uploadedCount = items.filter((item) => item.status === "done").length;
  const editableItems = items.filter(isEditable);
  const uploadableCount = editableItems.length;
  const anyHasDetails = editableItems.some((item) =>
    hasExtraDetails(item.details),
  );
  const formatMeta = (file: File) =>
    `${dateFormatter.format(file.lastModified)} • ${formatFileSize(file.size)}`;
  const isFull = items.length >= MAX_UPLOAD_FILES;

  const getProblemMessage = (item: UploadItem) => {
    if (item.problem === "tooLarge") {
      return t("collections.multiUpload.exceedsLimit", {
        max: MAX_UPLOAD_FILE_SIZE_MB,
      });
    }
    if (item.problem === "unsupportedType") {
      return t("collections.multiUpload.unsupportedType");
    }
    return item.errorMessage;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          isEditingDetails
            ? "gap-0 overflow-hidden p-0 sm:max-w-4xl"
            : "flex max-h-[90vh] flex-col gap-5 sm:max-w-xl",
        )}
        showCloseButton={!isEditingDetails}
        // Only close on explicit intent (✕, Cancel, Escape). The MUI menu that
        // opens this dialog restores focus to its anchor behind the overlay,
        // and toasts render outside it too — Radix treats both as an outside
        // interaction and would dismiss the dialog (and abort uploads).
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          // In the edit view Escape steps back instead of dropping the queue.
          if (isEditingDetails) {
            event.preventDefault();
            editViewRef.current?.handleEscape();
          }
        }}
      >
        {isEditingDetails ? (
          <EditFileDetailsView
            ref={editViewRef}
            files={editableItems}
            formatMeta={formatMeta}
            onSave={handleSaveDetails}
            onCancel={() => setIsEditingDetails(false)}
          />
        ) : (
          <>
        <DialogHeader>
          <DialogTitle>{t("collections.multiUpload.title")}</DialogTitle>
          <DialogDescription className="text-start">
            {t("collections.multiUpload.description", {
              max: MAX_UPLOAD_FILES,
              types: supportedTypes,
              size: MAX_UPLOAD_FILE_SIZE_MB,
            })}
          </DialogDescription>
          <p className="text-muted-foreground text-start text-xs">
            {t("collections.documentUploadUnsupported")}
          </p>
        </DialogHeader>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            "flex flex-col items-center gap-3 rounded-lg border border-dashed px-4 py-8 text-center transition-colors",
            isDragging && "border-primary bg-primary/5",
            isFull && "opacity-50",
          )}
        >
          <InboxIcon
            className="text-muted-foreground size-10"
            strokeWidth={1.25}
            aria-hidden
          />
          <p className="text-sm">{t("collections.multiUpload.dropzone")}</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={DOCUMENT_UPLOAD_ACCEPT}
            onChange={handleInputChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={isFull}
          >
            {t("collections.multiUpload.browse")}
          </Button>
        </div>

        {items.length > 0 && (
          <div className="flex min-h-0 flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium" aria-live="polite">
                {t("collections.multiUpload.progressCount", {
                  uploaded: uploadedCount,
                  total: items.length,
                })}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => setIsEditingDetails(true)}
                disabled={isUploading || editableItems.length === 0}
              >
                <PencilLineIcon />
                {t("collections.multiUpload.editDetails")}
              </Button>
            </div>
            {editableItems.length > 0 && !anyHasDetails && (
              <p className="flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium">
                <InfoIcon className="size-4 shrink-0" aria-hidden />
                {t("collections.multiUpload.detailsHint")}
              </p>
            )}
            <ul className="-me-1 flex min-h-0 flex-col gap-2 overflow-y-auto pe-1">
              {items.map((item) => {
                const problem = getProblemMessage(item);
                return (
                  <li
                    key={item.id}
                    className="flex items-center gap-3 rounded-lg border px-4 py-3"
                  >
                    <FileIcon
                      className="text-muted-foreground size-6 shrink-0"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {item.file.name}
                      </p>
                      {problem ? (
                        <p className="text-destructive flex items-center gap-1 text-xs">
                          <InfoIcon className="size-3.5 shrink-0" aria-hidden />
                          <span className="truncate">{problem}</span>
                        </p>
                      ) : (
                        <p className="text-muted-foreground text-xs">
                          {formatMeta(item.file)}
                        </p>
                      )}
                    </div>
                    {isEditable(item) &&
                      !hasExtraDetails(item.details) && (
                        <span className="text-muted-foreground hidden self-stretch border-s ps-4 text-xs sm:flex sm:items-center">
                          {t("collections.multiUpload.noDetails")}
                        </span>
                      )}
                    <div className="flex shrink-0 items-center gap-1">
                      <ItemActions
                        item={item}
                        disabled={isUploading}
                        onCancel={() => cancelItem(item.id)}
                        onRetry={() => handleRetry(item)}
                        onRemove={() => removeItem(item.id)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleUploadAll}
            disabled={isUploading || uploadableCount === 0}
          >
            {isUploading && <Spinner />}
            {isUploading
              ? t("collections.uploading")
              : t("collections.multiUpload.submit")}
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

type ItemActionsProps = {
  item: UploadItem;
  disabled: boolean;
  onCancel: () => void;
  onRetry: () => void;
  onRemove: () => void;
};

const ItemActions = ({
  item,
  disabled,
  onCancel,
  onRetry,
  onRemove,
}: ItemActionsProps) => {
  const { t } = useTranslation();

  if (item.status === "uploading") {
    return (
      <button
        type="button"
        onClick={onCancel}
        aria-label={t("collections.multiUpload.cancelUpload", {
          name: item.file.name,
        })}
        className="text-muted-foreground hover:text-foreground relative grid size-9 place-items-center rounded-full"
      >
        <ProgressRing progress={item.progress} />
        <XIcon className="size-4" aria-hidden />
      </button>
    );
  }

  if (item.status === "done") {
    return (
      <CheckCircle2Icon
        className="text-primary size-5"
        aria-label={t("collections.multiUpload.uploaded")}
      />
    );
  }

  const removeButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={onRemove}
      disabled={disabled}
      aria-label={t("collections.multiUpload.removeFile", {
        name: item.file.name,
      })}
      className="text-destructive hover:text-destructive"
    >
      <Trash2Icon className="size-5" />
    </Button>
  );

  if (item.status === "error") {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onRetry}
          disabled={disabled}
          aria-label={t("collections.multiUpload.retryFile", {
            name: item.file.name,
          })}
        >
          <RotateCwIcon className="size-5" />
        </Button>
        {removeButton}
      </>
    );
  }

  return removeButton;
};

// Upload progress hits 100% when the bytes are sent, before the server has
// accepted the document, so a full ring turns into a spinner until it answers.
const ProgressRing = ({ progress }: { progress: number }) => {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const isWaiting = progress >= 100;
  const visible = isWaiting ? 0.25 : progress / 100;

  return (
    <svg
      viewBox="0 0 36 36"
      className={cn("absolute inset-0 size-9 -rotate-90", isWaiting && "animate-spin")}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progress}
    >
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        strokeWidth="3"
        className="stroke-muted"
      />
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - visible)}
        className="stroke-primary transition-[stroke-dashoffset] duration-200"
      />
    </svg>
  );
};

export default UploadDocumentsDialog;
